import gsap from 'gsap'
import { DURATION, EASE } from '../../shared/motion.js'
import { device } from '../../shared/device.js'

const MOTION_CONFIG = {
    duration: DURATION.underline,
    ease: EASE.underline
}

const STATES = {
    PARKED_IN: { xPercent: -101, opacity: 0 },
    SHOWN: { xPercent: 0, opacity: 1 },
    ARMED_OUT: { xPercent: 0, opacity: 0 },
    PARKED_OUT: { xPercent: 101, opacity: 1 }
}

// ─────────────────────────────────────────────────────────────
// DOM Helpers
// ─────────────────────────────────────────────────────────────

export function createUnderline(btn) {
    if (btn._underline) {
        return btn._underline
    }

    if (getComputedStyle(btn).position === 'static') {
        btn.style.position = 'relative'
    }

    const track = document.createElement('span')
    Object.assign(track.style, {
        position: 'absolute',
        left: '0',
        bottom: '-1px',
        width: '100%',
        height: '1px',
        overflow: 'hidden',
        pointerEvents: 'none'
    })

    const createLine = () => {
        const line = document.createElement('span')
        Object.assign(line.style, {
            position: 'absolute',
            inset: '0',
            display: 'block',
            background: 'currentColor'
        })
        track.appendChild(line)
        return line
    }

    const lineIn = createLine()
    const lineOut = createLine()
    btn.appendChild(track)

    const instance = { lineIn, lineOut }
    btn._underline = instance

    gsap.set(lineIn, STATES.PARKED_IN)
    gsap.set(lineOut, STATES.PARKED_OUT)

    return instance
}

function _getOrInit(btn) {
    return btn._underline || createUnderline(btn)
}

function _cleanup(lineIn, lineOut) {
    gsap.killTweensOf([lineIn, lineOut])
}

// ─────────────────────────────────────────────────────────────
// Functional Motion API
// ─────────────────────────────────────────────────────────────

export const resetUnderline = (btn) => {
    const { lineIn, lineOut } = _getOrInit(btn)
    _cleanup(lineIn, lineOut)

    gsap.set(lineIn, STATES.PARKED_IN)
    return gsap.set(lineOut, STATES.PARKED_OUT)
}

export const showUnderline = (btn) => {
    const { lineIn, lineOut } = _getOrInit(btn)
    _cleanup(lineIn, lineOut)

    gsap.set(lineIn, STATES.SHOWN)
    return gsap.set(lineOut, STATES.ARMED_OUT)
}

/** Hover 進場：lineIn 從左滑向中央，lineOut 復位待命 */
export const drawIn = (btn) => {
    const { lineIn, lineOut } = _getOrInit(btn)

    // 停止正在退場的 lineOut
    gsap.killTweensOf(lineOut)
    gsap.set(lineOut, STATES.ARMED_OUT)

    // 清除 lineIn 的動態，準備畫進
    gsap.killTweensOf(lineIn)

    // 如果 lineIn 剛好在退場過程，確保 opacity 是 1
    gsap.set(lineIn, { opacity: 1 })

    return gsap.to(lineIn, {
        xPercent: STATES.SHOWN.xPercent,
        duration: MOTION_CONFIG.duration,
        ease: MOTION_CONFIG.ease
    })
}

/** Hover 離場：抓取 lineIn 當前位置，讓 lineOut 從該位置接力劃出 */
export const erase = (btn) => {
    const { lineIn, lineOut } = _getOrInit(btn)

    // 1. 取得 lineIn 當下的實際 xPercent 位置（防止中途硬斷）
    const currentX = gsap.getProperty(lineIn, 'xPercent')

    // 2. 停止 lineIn 的動畫並隱藏，重置到左側待命
    gsap.killTweensOf(lineIn)
    gsap.set(lineIn, STATES.PARKED_IN)

    // 3. 停止 lineOut 動畫，並讓它「瞬間接管」lineIn 當前的位置
    gsap.killTweensOf(lineOut)
    gsap.set(lineOut, {
        xPercent: currentX,
        opacity: 1
    })

    // 4. 計算剩餘距離的比率，動態調整 duration（確保速度恆定，不會突然暴衝）
    const remainingRatio = Math.max(0.2, (STATES.PARKED_OUT.xPercent - currentX) / 202)
    const duration = MOTION_CONFIG.duration * remainingRatio

    // 5. 讓 lineOut 從目前位置順暢滑出至右邊
    return gsap.to(lineOut, {
        xPercent: STATES.PARKED_OUT.xPercent,
        duration: duration,
        ease: MOTION_CONFIG.ease
    })
}

// ─────────────────────────────────────────────────────────────
// Stateful Manager & Export
// ─────────────────────────────────────────────────────────────

export class UnderlineFx {
    constructor(btn) {
        this.btn = btn
        createUnderline(btn)
    }

    drawIn() {
        return drawIn(this.btn)
    }

    erase() {
        return erase(this.btn)
    }

    show() {
        return showUnderline(this.btn)
    }

    reset() {
        return resetUnderline(this.btn)
    }
}

export const attachHoverUnderline = (btn) => {
    const fx = new UnderlineFx(btn)

    btn.addEventListener('mouseenter', () => {
        if (!device.hasHover) {
            return
        }
        return fx.drawIn()
    })
    btn.addEventListener('mouseleave', () => {
        if (!device.hasHover) {
            return
        }
        return fx.erase()
    })

    return fx
}
